import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { Check, ChevronDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { QuantityStepper } from "@/components/QuantityStepper";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { formatPrice, loc } from "@/lib/format";
import type { Addition, AdditionGroup, MenuItem, Variation } from "@/lib/types";
import { missingRequiredAdditions, validAdditions } from "@/lib/menu-model";
import { cn } from "@/lib/utils";
import { MenuItemImage } from "@/components/menu/MenuItemImage";
import { PriceDisplay } from "@/components/menu/PriceDisplay";
import { DiscountBadge } from "@/components/menu/DiscountBadge";
import { MenuItemPreviewDialog } from "@/components/menu/MenuItemPreviewDialog";

interface Props {
  item: MenuItem;
  mode?: "online" | "indoor" | undefined;
  onRequireOrderType?: (() => boolean) | undefined;
}

export function ProductCard({
  item,
  mode = "online",
  onRequireOrderType,
}: Props) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const readOnly = mode === "indoor";

  const priceOf = (v: { price: number; branchPrice: number }) =>
    readOnly ? v.branchPrice : v.price;
  const priceSource =
    item.type === "variable"
      ? item.variations.length
        ? item.variations.reduce((best, candidate) =>
            priceOf(candidate) < priceOf(best) ? candidate : best,
          )
        : item
      : item;
  const image = (
    <>
      <MenuItemImage
        source={item.responsiveImage ?? item.image}
        name={loc(item.name, lang)}
        sizes="(max-width: 639px) 104px, 144px"
        loading="lazy"
        className="size-full object-cover"
      />
      {!readOnly ? (
        <DiscountBadge
          price={priceOf(priceSource)}
          originalPrice={priceSource.originalPrice}
        />
      ) : null}
    </>
  );

  return (
    <article className="surface-card relative overflow-hidden">
      {readOnly ? (
        <button
          type="button"
          className="absolute inset-0 z-10 cursor-pointer rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary"
          onClick={() => setOpen(true)}
          aria-label={loc(item.name, lang)}
        />
      ) : null}
      <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-3 p-4 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-5 sm:p-5">
        {readOnly ? (
          <div className="relative block size-26 overflow-hidden rounded-xl sm:size-36 sm:rounded-2xl">{image}</div>
        ) : (
          <Link to="/menu/$slug" params={{ slug: item.slug }} className="relative block size-26 overflow-hidden rounded-xl sm:size-36 sm:rounded-2xl">{image}</Link>
        )}

        <div className="flex min-w-0 flex-col items-start gap-2 sm:flex-row sm:justify-between sm:gap-3">
          <div className="min-w-0">
            {readOnly ? (
              <h3 className="break-words text-base font-bold leading-snug sm:text-lg">
                {loc(item.name, lang)}
              </h3>
            ) : (
              <Link
                to="/menu/$slug"
                params={{ slug: item.slug }}
                className="block"
              >
                <h3 className="break-words text-base font-bold leading-snug sm:text-lg hover:text-primary">
                  {loc(item.name, lang)}
                </h3>
              </Link>
            )}
            <p className="line-clamp-2 text-sm text-muted-foreground">
              {loc(item.description, lang)}
            </p>
          </div>
          <p className="shrink-0 text-start text-base font-extrabold text-foreground">
            {item.type === "variable" ? (
              <span className="block text-xs font-medium text-muted-foreground">
                {t("common.from")}
              </span>
            ) : null}
            <PriceDisplay
              price={priceOf(priceSource)}
              originalPrice={readOnly ? null : priceSource.originalPrice}
            />
          </p>
        </div>

        {(!readOnly || !item.indoorAvailable) && (
          <div className="col-span-full">
            {readOnly ? (
              <p className="text-sm font-bold text-muted-foreground">
                {t("menu.unavailable")}
              </p>
            ) : !item.onlineAvailable ? (
              <p className="text-sm font-bold text-muted-foreground">
                {t("menu.unavailable")}
              </p>
            ) : item.type === "variable" ? (
              <Button
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => setOpen(true)}
              >
                {t("menu.viewVariations")}
              </Button>
            ) : (
              <SimpleConfigurator
                item={item}
                onRequireOrderType={onRequireOrderType}
              />
            )}
          </div>
        )}
      </div>

      {readOnly ? (
        <MenuItemPreviewDialog item={item} open={open} onOpenChange={setOpen} priceMode="indoor" />
      ) : (
        <VariationDialog
          item={item}
          open={open}
          onOpenChange={setOpen}
          onRequireOrderType={onRequireOrderType}
        />
      )}
    </article>
  );
}

function useAddToCart(onRequireOrderType?: () => boolean) {
  const { addLine, cart } = useCart();
  const { t } = useI18n();
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const running = useRef(false);
  const reset = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(reset.current), []);

  const run = async (build: () => Parameters<typeof addLine>[0]) => {
    if (!cart.orderType?.branchId) {
      if (onRequireOrderType) onRequireOrderType();
      else toast.error(t("menu.orderTypeRequired"));
      return;
    }
    if (running.current) return;
    running.current = true;
    setLoading(true);
    setDone(false);
    try {
      await addLine(build());
      setDone(true);
      toast.success(t("menu.added"));
      clearTimeout(reset.current);
      reset.current = setTimeout(() => setDone(false), 1600);
    } catch {
      // CartProvider presents a safe, localized global error and leaves the cart unchanged.
    } finally {
      running.current = false;
      setLoading(false);
    }
  };

  return { run, loading, done };
}

export function AdditionsPicker({
  groups,
  selected,
  onChange,
  idPrefix,
}: {
  groups: AdditionGroup[];
  selected: Addition[];
  onChange: (additions: Addition[]) => void;
  idPrefix: string;
}) {
  const { t, lang } = useI18n();
  if (groups.length === 0) return null;
  return (
    <div className="grid gap-4">
      {groups.map((group) => {
        const groupSelection = selected.filter(
          (addition) => addition.groupId === group.id,
        );
        const requiredSingleChoice = group.minimum === 1 && group.maximum === 1;
        const selectionHint =
          group.minimum === 0
            ? t("menu.chooseUpTo", { count: group.maximum })
            : group.minimum === group.maximum
              ? t("menu.chooseExactly", { count: group.minimum })
              : t("menu.chooseRange", {
                  minimum: group.minimum,
                  maximum: group.maximum,
                });
        const missing = groupSelection.length < group.minimum;
        return (
          <fieldset key={group.id} className="grid gap-2">
            <legend className="mb-1 text-sm font-bold">
              {loc(group.name, lang)}{" "}
              <span className="ms-2 text-xs font-medium text-muted-foreground">
                {selectionHint}
              </span>
            </legend>
            {requiredSingleChoice ? (
              <RadioGroup
                value={groupSelection[0]?.id ?? ""}
                onValueChange={(addonId) => {
                  const addition = group.additions.find(
                    (candidate) => candidate.id === addonId,
                  );
                  if (!addition) return;
                  onChange([
                    ...selected.filter(
                      (candidate) => candidate.groupId !== group.id,
                    ),
                    addition,
                  ]);
                }}
                className="grid gap-2 sm:grid-cols-2"
              >
                {group.additions.map((addition) => {
                  const id = `${idPrefix}-${addition.id}`;
                  return (
                    <label
                      key={addition.id}
                      htmlFor={id}
                      className="flex cursor-pointer items-center gap-3 rounded-2xl border border-border bg-secondary/50 px-3 py-2.5 text-sm transition-colors hover:border-primary/40"
                    >
                      <RadioGroupItem id={id} value={addition.id} />
                      <span className="flex-1">{loc(addition.name, lang)}</span>
                      <span className="shrink-0 font-bold text-foreground">
                        {addition.price === 0
                          ? t("menu.included")
                          : `+${formatPrice(addition.price, lang)}`}
                      </span>
                    </label>
                  );
                })}
              </RadioGroup>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {group.additions.map((addition) => {
                  const id = `${idPrefix}-${addition.id}`;
                  const checked = groupSelection.some(
                    (candidate) => candidate.id === addition.id,
                  );
                  const disabled =
                    !checked && groupSelection.length >= group.maximum;
                  return (
                    <label
                      key={addition.id}
                      htmlFor={id}
                      className={cn(
                        "flex items-center gap-3 rounded-2xl border border-border bg-secondary/50 px-3 py-2.5 text-sm transition-colors",
                        disabled
                          ? "cursor-not-allowed opacity-50"
                          : "cursor-pointer hover:border-primary/40",
                      )}
                    >
                      <Checkbox
                        id={id}
                        checked={checked}
                        disabled={disabled}
                        onCheckedChange={() =>
                          onChange(
                            checked
                              ? selected.filter(
                                  (candidate) => candidate.id !== addition.id,
                                )
                              : [...selected, addition],
                          )
                        }
                      />
                      <span className="flex-1">{loc(addition.name, lang)}</span>
                      <span className="shrink-0 font-bold text-foreground">
                        {addition.price === 0
                          ? t("menu.included")
                          : `+${formatPrice(addition.price, lang)}`}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            {missing ? (
              <p className="text-xs font-medium text-destructive" role="status">
                {t("menu.requiredSelectionMissing", { count: group.minimum })}
              </p>
            ) : null}
          </fieldset>
        );
      })}
    </div>
  );
}

export function SimpleConfigurator({
  item,
  onRequireOrderType,
  expanded = false,
}: {
  item: MenuItem;
  onRequireOrderType?: (() => boolean) | undefined;
  expanded?: boolean | undefined;
}) {
  const { t, lang } = useI18n();
  const [showOptions, setShowOptions] = useState(
    expanded || item.additionGroups.some((group) => group.minimum > 0),
  );
  const optionsId = useId();
  const [selected, setSelected] = useState<Addition[]>([]);
  const [note, setNote] = useState("");
  const [qty, setQty] = useState(1);
  const { run, loading, done } = useAddToCart(onRequireOrderType);
  const selectionValid = validAdditions(item.additionGroups, selected);

  const total = useMemo(
    () => (item.price + selected.reduce((s, a) => s + a.price, 0)) * qty,
    [item.price, selected, qty],
  );

  return (
    <div className="grid gap-3">
      {!expanded ? (
        <button
          type="button"
          onClick={() => setShowOptions((v) => !v)}
          aria-expanded={showOptions}
          aria-controls={optionsId}
          className="inline-flex min-h-11 w-fit items-center gap-1 text-sm font-bold text-primary hover:underline"
        >
          {t("menu.customize")}
          <ChevronDown
            className={cn(
              "size-4 transition-transform",
              showOptions && "rotate-180",
            )}
          />
        </button>
      ) : null}

      {showOptions ? (
        <div id={optionsId} className="grid gap-3">
          <AdditionsPicker
            groups={item.additionGroups}
            selected={selected}
            onChange={setSelected}
            idPrefix={`simple-${item.id}`}
          />
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            placeholder={t("common.notePlaceholder")}
            aria-label={t("common.note")}
            className="min-h-16 rounded-2xl"
          />
        </div>
      ) : null}

      {!selectionValid ? (
        <p className="text-sm font-medium text-primary">
          {t(
            missingRequiredAdditions(item.additionGroups, selected)
              ? "menu.requiredOptionsMissing"
              : "menu.tooManyOptions",
          )}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <QuantityStepper
          value={qty}
          onChange={setQty}
          label={`${t("common.quantity")} · ${loc(item.name, lang)}`}
        />
        <Button
          disabled={loading || !item.onlineAvailable || !selectionValid}
          onClick={() =>
            void run(() => ({
              itemId: item.id,
              slug: item.slug,
              name: item.name,
              image: item.image,
              unitPrice: item.price,
              additions: selected,
              ...(note ? { note } : {}),
              quantity: qty,
            }))
          }
        >
          {done ? <Check className="size-4" /> : <Plus className="size-4" />}
          {done ? t("menu.added") : t("menu.addToCart")}
          <span className="opacity-80">· {formatPrice(total, lang)}</span>
        </Button>
      </div>
    </div>
  );
}

/** Inline purchase controls for the full product page. Menu cards keep their compact configurator. */
export function ProductDetailConfigurator({
  item,
  variation,
  onVariationChange,
  onRequireOrderType,
}: {
  item: MenuItem;
  variation?: Variation | undefined;
  onVariationChange?: ((id: string) => void) | undefined;
  onRequireOrderType?: (() => boolean) | undefined;
}) {
  const { t, lang } = useI18n();
  const [selected, setSelected] = useState<Addition[]>([]);
  const [note, setNote] = useState("");
  const [qty, setQty] = useState(1);
  const { run, loading, done } = useAddToCart(onRequireOrderType);
  const groups = variation?.additionGroups ?? item.additionGroups;
  const selectionValid = validAdditions(groups, selected);
  const total =
    ((variation?.price ?? item.price) +
      selected.reduce((sum, addition) => sum + addition.price, 0)) *
    qty;

  return (
    <div className="grid gap-6">
      {item.type === "variable" && item.variations.length > 0 ? (
        <fieldset className="grid gap-3">
          <legend className="mb-2 text-sm font-bold text-foreground">
            {t("menu.chooseVariation")}
          </legend>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {item.variations.map((option) => (
              <button
                key={option.id}
                type="button"
                aria-pressed={variation?.id === option.id}
                onClick={() => {
                  if (variation?.id === option.id) return;
                  setSelected([]);
                  onVariationChange?.(option.id);
                }}
                className={cn(
                  "pressable flex min-h-16 flex-col items-start justify-center gap-1 rounded-2xl border px-4 py-3 text-start transition-colors",
                  variation?.id === option.id
                    ? "border-primary bg-primary/10 text-foreground ring-1 ring-primary"
                    : "border-border bg-card text-foreground hover:border-primary/50",
                )}
              >
                <span className="text-sm font-bold leading-tight">
                  {loc(option.name, lang)}
                </span>
                <span className="text-xs font-semibold text-muted-foreground">
                  {formatPrice(option.price, lang)}
                </span>
              </button>
            ))}
          </div>
          {variation &&
          loc(variation.description, lang) &&
          loc(variation.description, lang) !== loc(item.description, lang) ? (
            <p className="text-sm leading-relaxed text-muted-foreground">
              {loc(variation.description, lang)}
            </p>
          ) : null}
        </fieldset>
      ) : null}

      {groups.length > 0 ? (
        <div className="border-t border-border pt-5">
          <AdditionsPicker
            groups={groups}
            selected={selected}
            onChange={setSelected}
            idPrefix={`detail-${item.id}-${variation?.id ?? "simple"}`}
          />
        </div>
      ) : null}

      <details className="group border-t border-border pt-4">
        <summary className="inline-flex min-h-11 cursor-pointer list-none items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground [&::-webkit-details-marker]:hidden">
          <ChevronDown
            className="size-4 transition-transform group-open:rotate-180"
            aria-hidden="true"
          />
          {t("common.note")}{" "}
          <span className="font-normal">({t("common.optional")})</span>
        </summary>
        <Textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={1000}
          placeholder={t("common.notePlaceholder")}
          aria-label={t("common.note")}
          className="mt-2 min-h-20 rounded-2xl"
        />
      </details>

      {!selectionValid && !missingRequiredAdditions(groups, selected) ? (
        <p className="text-sm font-medium text-destructive" role="status">
          {t("menu.tooManyOptions")}
        </p>
      ) : null}

      <div className="flex min-w-0 items-stretch gap-2 border-t border-border pt-5 sm:gap-3">
        <QuantityStepper
          value={qty}
          onChange={setQty}
          label={`${t("common.quantity")} · ${loc(item.name, lang)}`}
          size="compact"
          className="shrink-0"
        />
        <Button
          size="lg"
          className="h-auto min-h-12 min-w-0 flex-1 gap-1.5 whitespace-normal px-2 py-2 text-xs leading-tight sm:px-5 sm:text-base"
          disabled={
            loading ||
            !item.onlineAvailable ||
            !selectionValid ||
            (item.type === "variable" && !variation)
          }
          onClick={() =>
            void run(() => ({
              itemId: item.id,
              slug: item.slug,
              name: item.name,
              image: variation?.image || item.image,
              ...(variation
                ? { variationId: variation.id, variationName: variation.name }
                : {}),
              unitPrice: variation?.price ?? item.price,
              additions: selected,
              ...(note.trim() ? { note: note.trim() } : {}),
              quantity: qty,
            }))
          }
        >
          {done ? <Check aria-hidden="true" /> : <Plus aria-hidden="true" />}
          <span>{done ? t("menu.added") : t("menu.addToCart")}</span>
          <span className="opacity-70" aria-hidden="true">·</span>
          <span className="tabular-nums">{formatPrice(total, lang)}</span>
        </Button>
      </div>
    </div>
  );
}

export function VariationDialog({
  item,
  open,
  onOpenChange,
  readOnly,
  onRequireOrderType,
}: {
  item: MenuItem;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  readOnly?: boolean | undefined;
  onRequireOrderType?: (() => boolean) | undefined;
}) {
  const { t, lang } = useI18n();
  const [variationId, setVariationId] = useState(item.variations[0]?.id ?? "");
  const [selected, setSelected] = useState<Addition[]>([]);
  const [note, setNote] = useState("");
  const [qty, setQty] = useState(1);
  const { run, loading, done } = useAddToCart(onRequireOrderType);

  const variation: Variation | undefined =
    item.variations.find((v) => v.id === variationId) ?? item.variations[0];

  const price = variation
    ? readOnly
      ? variation.branchPrice
      : variation.price
    : 0;
  const total = (price + selected.reduce((s, a) => s + a.price, 0)) * qty;
  const selectionValid = variation
    ? validAdditions(variation.additionGroups, selected)
    : false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">{loc(item.name, lang)}</DialogTitle>
          <DialogDescription>{t("menu.optionsHint")}</DialogDescription>
        </DialogHeader>

        {variation ? (
          <div className="grid gap-4">
            <MenuItemImage
              source={
                variation.responsiveImage ??
                variation.image ??
                item.responsiveImage ??
                item.image
              }
              name={loc(variation.name, lang)}
              sizes="(max-width: 767px) calc(100vw - 3rem), 640px"
              loading="lazy"
              className="h-auto w-full rounded-3xl"
              fallbackClassName="h-52"
            />
            <div className="flex flex-wrap gap-2">
              {item.variations.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => {
                    setVariationId(v.id);
                    setSelected([]);
                  }}
                  aria-pressed={v.id === variation.id}
                  className={cn(
                    "pressable min-h-11 rounded-full border px-4 py-2 text-sm font-bold",
                    v.id === variation.id
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card hover:bg-accent",
                  )}
                >
                  {loc(v.name, lang)}
                </button>
              ))}
            </div>
            <p className="line-clamp-3 text-sm text-muted-foreground">
              {loc(variation.description, lang)}
            </p>
            <p className="text-xl font-extrabold text-foreground">
              <PriceDisplay
                price={price}
                originalPrice={variation.originalPrice}
              />
            </p>

            {readOnly ? (
              <p className="rounded-2xl bg-secondary px-4 py-3 text-sm text-muted-foreground">
                {t("branchMenu.readonly")}
              </p>
            ) : (
              <>
                <AdditionsPicker
                  groups={variation.additionGroups}
                  selected={selected}
                  onChange={setSelected}
                  idPrefix={`var-${variation.id}`}
                />
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={1000}
                  placeholder={t("common.notePlaceholder")}
                  aria-label={t("common.note")}
                  className="min-h-16 rounded-2xl"
                />
                {!selectionValid ? (
                  <p className="text-sm font-medium text-primary">
                    {t(
                      missingRequiredAdditions(
                        variation.additionGroups,
                        selected,
                      )
                        ? "menu.requiredOptionsMissing"
                        : "menu.tooManyOptions",
                    )}
                  </p>
                ) : null}
                <div className="sticky -bottom-6 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-background py-4">
                  <QuantityStepper value={qty} onChange={setQty} />
                  <Button
                    size="lg"
                    disabled={
                      loading || !selectionValid || !item.onlineAvailable
                    }
                    onClick={() =>
                      void run(() => ({
                        itemId: item.id,
                        slug: item.slug,
                        name: item.name,
                        image: variation.image ?? item.image,
                        variationId: variation.id,
                        variationName: variation.name,
                        unitPrice: variation.price,
                        additions: selected,
                        ...(note ? { note } : {}),
                        quantity: qty,
                      }))
                    }
                  >
                    {done ? (
                      <Check className="size-4" />
                    ) : (
                      <Plus className="size-4" />
                    )}
                    {done ? t("menu.added") : t("menu.addToCart")} ·{" "}
                    {formatPrice(total, lang)}
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
